import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import api from "../api";
import ReactGA from "react-ga4";
import Form from "react-bootstrap/Form";
import { isMobile } from "react-device-detect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faUpload, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Button, Container, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const CameraCapture = ({ event_id, setMatchGallery, setShowMatch, setMatchInfo }) => {
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [tolerance, setTolerance] = useState(0.52);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const showPrecision = false;

  const videoConstraints = {
    width: { ideal: 1260 },
    height: { ideal: 720 },
    facingMode: isMobile ? { exact: "user" } : "user",
  };

  const handleShowMatchGallery = (images) => {
    if (images) {
      setMatchGallery(images);
      setShowMatch(true);
      setImage(null);
    } else {
      setMatchGallery([]);
      setShowMatch(false);
    }
  };

  const resetCapture = () => {
    setImage(null);
    handleShowMatchGallery(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const openCamera = () => {
    setIsCameraOpen(true);
    resetCapture();
  };

  const closeCamera = () => {
    setIsCameraOpen(false);
  };

  const capture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setImage(imageSrc);
    closeCamera();
    getImageMatch(imageSrc);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result);
        getImageMatch(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const getImageMatch = async (imageData) => {
    setIsUploading(true);
    handleShowMatchGallery(null);

    try {
      const mimeType = imageData.match(/^data:(image\/[a-zA-Z]+);base64,/)[1];
      const extension = mimeType.split("/")[1];
      const imageName = `image_${Date.now()}.${extension}`;
      const blob = await fetch(imageData).then((res) => res.blob());

      const formData = new FormData();
      formData.append("picture", blob, imageName);

      const response = await api.post(
        `/get_match?eid=${event_id}&tolerance=${tolerance}`,
        formData,
        {
          timeout: 10000,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setMatchInfo(response.data.infos);
      const found = response.data.images && response.data.images.length > 0;
      ReactGA.event({ category: 'guest', action: 'face_search_completed', label: found ? 'found' : 'not_found' });
      handleShowMatchGallery(response.data.images);
    } catch (error) {
      console.error("Error uploading image:", error);
      if (error.response?.data) {
        alert(error.response.data.error);
      } else {
        alert("An error occurred. Please try again later.");
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{ width: "100%", maxWidth: "480px", margin: "0 auto", padding: "0 16px" }}>

      {/* Camera feed */}
      {isCameraOpen && (
        <div className="d-flex flex-column align-items-center">
          <div style={{ width: "100%", aspectRatio: "4/3", borderRadius: "16px", overflow: "hidden" }}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
          <button
            onClick={capture}
            style={{
              marginTop: "20px",
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: "#111",
              border: "4px solid #fff",
              boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon icon={faCamera} color="#fff" size="lg" />
          </button>
        </div>
      )}

      {/* Idle state */}
      {!isCameraOpen && !isUploading && (
        <div
          style={{
            border: "1.5px dashed #ddd",
            borderRadius: "16px",
            padding: "40px 24px",
            textAlign: "center",
            background: "#fafafa",
          }}
        >
          <p style={{ fontSize: "13px", color: "#aaa", marginBottom: "28px", letterSpacing: "0.03em" }}>
            {t("camera_capture.take_selfie")}{!isMobile && ` · ${t("camera_capture.upload_photo")}`}
          </p>

          <div className="d-flex justify-content-center gap-3">
            <button
              onClick={openCamera}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                padding: "18px 28px",
                borderRadius: "12px",
                border: "1.5px solid #e0e0e0",
                background: "#fff",
                cursor: "pointer",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                transition: "box-shadow 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)")}
              onMouseOut={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)")}
            >
              <FontAwesomeIcon icon={faCamera} size="lg" style={{ color: "#555" }} />
              <span style={{ fontSize: "12px", color: "#555", fontWeight: 500 }}>
                {t("camera_capture.click_me")}
              </span>
            </button>

            {!isMobile && (
              <button
                onClick={openFilePicker}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  padding: "18px 28px",
                  borderRadius: "12px",
                  border: "1.5px solid #e0e0e0",
                  background: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  transition: "box-shadow 0.2s",
                }}
                onMouseOver={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)")}
                onMouseOut={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)")}
              >
                <FontAwesomeIcon icon={faUpload} size="lg" style={{ color: "#555" }} />
                <span style={{ fontSize: "12px", color: "#555", fontWeight: 500 }}>
                  {t("camera_capture.upload_photo")}
                </span>
              </button>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
            onClick={resetCapture}
          />
        </div>
      )}

      {/* Uploading state */}
      {isUploading && (
        <div style={{ textAlign: "center", padding: "48px 24px" }}>
          <Spinner animation="border" size="sm" style={{ color: "#888" }} />
          <p style={{ marginTop: "14px", fontSize: "13px", color: "#999", letterSpacing: "0.03em" }}>
            {t("camera_capture.looking_for_photos")}
          </p>
        </div>
      )}
    </div>
  );
};

export default CameraCapture;
